import { a3Copy } from "@/components/assignments/a3-copy";
import { useCallback, useContext, useLayoutEffect, useRef, type ReactNode } from "react";
import { UNSAFE_NavigationContext } from "react-router";
import { alsoDiscards, registerLeaveGuard } from "./assignment-draft-pop-interceptor";
import { DraftContext, type Draft, type Guard } from "./use-assignment-draft";

const WRITE_IN_PROGRESS = a3Copy.writeStillInProgress;

/** Bounded BrowserRouter adapter. No data-router blocker or router migration.
 * The leave coordinator refuses, restores, and replays navigation; this guard
 * reports its drafts and asks with a native confirm when it is the one asked.
 */
export function AssignmentDraftGuard({ children, confirmMessage = a3Copy.discardUnsavedChangesAndLeaveThisRecord, draftLabel = a3Copy.yourUnsavedAssignmentDraft }: { children: ReactNode; confirmMessage?: string; draftLabel?: string }) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const drafts = useRef(new Map<symbol, Draft>());
  const allow = useCallback(() => {
    const states = [...drafts.current.values()];
    if (states.some(state => state.pending)) {
      window.alert(WRITE_IN_PROGRESS);
      return false;
    }
    return !states.some(state => state.dirty)
      || window.confirm(confirmMessage);
  }, [confirmMessage]);
  useLayoutEffect(() => {
    const states = () => [...drafts.current.values()];
    const unregister = registerLeaveGuard(navigator, {
      label: draftLabel,
      state: () => states().some(state => state.pending) ? "busy" : states().some(state => state.dirty) ? "unsaved" : "clean",
      ask: (also, proceed) => {
        if (states().some(state => state.pending)) { window.alert(WRITE_IN_PROGRESS); return; }
        if (window.confirm(also.length ? `${confirmMessage} ${alsoDiscards(also)}` : confirmMessage)) proceed();
      },
    });
    const unload = (event: BeforeUnloadEvent) => {
      if (states().some(state => state.dirty || state.pending)) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", unload);
    return () => {
      unregister();
      window.removeEventListener("beforeunload", unload);
    };
  }, [confirmMessage, draftLabel, navigator]);
  const guard = useRef<Guard>({ drafts: drafts.current, allow });
  return <DraftContext.Provider value={guard.current}>{children}</DraftContext.Provider>;
}
